// AI Calorie Target — back-solves empirical TDEE from logged data and asks
// Gemini for a personalised daily kcal + macro target. Lives in Goals tab
// directly below Body Composition. Cross-cuts with Calories: applying a
// target writes through to calorieGoals so the daily ring uses it as the
// denominator.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { callGemini } from './geminiAI';
import AICostBadge from './hayah/AICostBadge';
import AIAnalysingPipeline from './hayah/AIAnalysingPipeline';
import { publishSources, calorieTargetSources } from './hayah/pipelineSources';

const STORAGE_KEY    = 'sirat.goals.calorieTarget';
const HISTORY_KEY    = 'sirat.goals.calorieTarget.history';
const LAST_AUTO_KEY  = 'sirat.goals.calorieTarget.lastAutoRun';
const PREFS_KEY      = 'sirat.goals.calorieTarget.prefs';
const PREDICTION_KEY = 'sirat.goals.calorieTarget.prediction';
const HISTORY_CAP    = 26;

const SYSTEM_PROMPT = `You are a body recomposition specialist. The user's empirical TDEE, requested daily target, BMR, observed weight-loss rate and macro history have ALREADY been computed deterministically from their logged data and are provided in the input under "computed". DO NOT recompute these numbers — trust them exactly. Your job is judgment: assess safety, choose a recovery path if needed, set macros, and explain. The weekly target refers to SCALE weight (total bodyweight), not fat specifically.

The "computed" block gives you:
  - empiricalTDEE: authoritative maintenance calories. Derived from the energy-balance method (avg intake − weight-trend slope × 7700) reconciled with measured Apple Health burn. USE THIS as TDEE.
  - tdeeBalance / tdeeActivity / tdee_divergence_pct: the two independent estimates and how far apart they are. If divergence > 15%, note it (intake under-logging or inflated device burn).
  - formulaTDEE + deviation_pct: Mifflin-St Jeor × activity, for reference only.
  - requestedTarget: empiricalTDEE − (target_loss_kg_per_week × 7700 ÷ 7), already rounded. This is the target IF safe.
  - bmr: Mifflin-St Jeor BMR (no activity multiplier) — the hard floor.
  - observed_weekly_rate_kg: the actual measured scale-weight slope (negative = losing). Compare against the request.
  - weight_readings + span_days: the number of WEIGH-INS the slope was fit through, and the calendar span they cover. These are NOT the same as intake days. When you flag the rate as unreliable, refer to the number of WEIGH-INS (e.g. "based on only 3 weigh-ins"), never "days of data" — the user logs food far more often than they weigh in, so conflating the two reads as a contradiction.
  - weight_trend_confidence: 0–1; low means too few/too-spread weigh-ins, so lean on measured burn and be conservative.

Your job:
1. Read empiricalTDEE and requestedTarget from "computed". Use body composition for context: if BF% dropped while weight dropped, loss is mostly fat (good). If lean mass dropped, flag muscle loss. If BF% rose while weight dropped, the deficit is too aggressive.

2. Note any TDEE deviation: if deviation_pct > 20% OR tdee_divergence_pct > 15%, surface it as a flag (likely under-reported intake or over-estimated device burn). Do not change empiricalTDEE.

3. The REQUESTED target is computed.requestedTarget — do not recompute it.

4. Assess safety of requestedTarget. Verdict = not_recommended if ANY apply:
   - requested_target < BMR (Mifflin-St Jeor BMR, no activity multiplier)
   - Deficit > 25% of empirical TDEE
   - Body fat already low (<12% M / <20% F) AND user is requesting 1kg/week
   - Lean mass trending down faster than fat mass
   Verdict = caution if:
   - Deficit 20-25% of TDEE
   - Logged weight not moving despite logged deficit (likely under-reporting)
   - Weight is stable but BF% drifting upward
   Otherwise verdict = safe.

5. ALWAYS produce a 'recommendation' object — this is the most important field. It is the user's actual instruction. Choose the primary path based on the user's data:

   For VERDICT = safe:
   - path = "proceed"
   - target_kcal = requested_target
   - rationale = 1 sentence on why this is sustainable for THIS user

   For VERDICT = caution or not_recommended, choose ONE primary path:

   a) path = "eat_more" — if user has low activity (<10k steps), high stress signals (lean loss), low BF margin, or simply needs slower rate:
      - target_kcal = max(BMR, empirical_TDEE − 0.20 × empirical_TDEE) rounded to nearest 10
      - expected_loss_kg_per_week = (empirical_TDEE − target_kcal) × 7 ÷ 7700
      - eta_date = today + (kg_to_goal ÷ expected_loss_kg_per_week) × 7 days
      - rationale: "Eat at <target> kcal — keeps you above BMR, ~<rate> kg/wk, preserves lean mass."

   b) path = "move_more" — if user is already active enough that the cost is small, or lean mass is dropping (more activity helps preserve it):
      - target_kcal = max(BMR + 50, requested_target_kcal that respects BMR floor)
      - extra_activity_kcal_per_day = needed extra burn to make requested loss rate safe (= (empirical_TDEE_needed − empirical_TDEE_current), keep <=400)
      - extra_activity_steps_per_day ≈ extra_activity_kcal_per_day × 20 (rule of thumb)
      - rationale: "Add ~<X> kcal/day activity (~<Y> steps or <Z> min brisk walk). Keeps deficit safe while preserving rate."

   c) path = "recomp" — only if user is near goal weight (<3 kg away) or BF is already low: maintain at TDEE, eat high protein, focus on training. target_kcal = empirical_TDEE.

   When primary is "eat_more", ALSO populate alternative with a "move_more" path (and vice versa) — give the user both viable options. Skip alternative for "proceed".

6. Recommend protein floor: 1.8–2.2 g/kg lean mass (or 2.0–2.4 g/kg lean if deficit > 15% TDEE — protects muscle).

7. Macro split (grams) using the chosen recommendation's target_kcal (NOT the unsafe requested target):
   - Hit protein floor
   - Fat ≥ 0.6 g/kg bodyweight (hormonal health)
   - Respect user's historical macro pattern (if they historically eat low-carb, lean low-carb; if balanced, lean balanced)
   - Sum macros so 4·protein + 4·carbs + 9·fat ≈ target_kcal (within 50 kcal)

8. dailyTarget MUST equal recommendation.primary.target_kcal — never the unsafe requested value. The card auto-applies dailyTarget to the user's actual calorie ring.

9. do_not: emit 2-3 specific anti-patterns to avoid, in plain English, tied to THIS user's situation. E.g. "Don't push to 1,400 kcal — lean mass already trending down, will accelerate." Not generic platitudes.

10. Project realistic ETA to goal weight at the RECOMMENDED rate (not the requested rate).

OUTPUT: Return ONLY valid JSON, no prose, no markdown fences.

Tone: direct, quantitative, no hedging. Reference actual numbers from the user's data when justifying decisions. The user has a quant background.`;

const RESPONSE_SCHEMA = {
  type: 'object',
  properties: {
    empiricalTDEE: { type: 'number' },
    formulaTDEE: { type: 'number' },
    deviation_pct: { type: 'number' },
    deviation_note: { type: 'string', nullable: true },
    dailyTarget: { type: 'number' },
    deficit: { type: 'number' },
    safety: {
      type: 'object',
      properties: {
        verdict: { type: 'string' }, // 'safe' | 'caution' | 'not_recommended'
        flags: { type: 'array', items: { type: 'string' } },
        recommendedAlternative: { type: 'string', nullable: true },
      },
      required: ['verdict', 'flags'],
    },
    protein_g: { type: 'number' },
    macros: {
      type: 'object',
      properties: {
        protein_g: { type: 'number' },
        carbs_g: { type: 'number' },
        fat_g: { type: 'number' },
      },
      required: ['protein_g', 'carbs_g', 'fat_g'],
    },
    projection: {
      type: 'object',
      properties: {
        weeks_to_goal: { type: 'number' },
        goal_date: { type: 'string' },
      },
      required: ['weeks_to_goal', 'goal_date'],
    },
    explanation: { type: 'string' },
    recommendation: {
      type: 'object',
      properties: {
        primary: {
          type: 'object',
          properties: {
            path: { type: 'string' }, // 'proceed' | 'eat_more' | 'move_more' | 'recomp'
            target_kcal: { type: 'number' },
            extra_activity_kcal_per_day: { type: 'number', nullable: true },
            extra_activity_steps_per_day: { type: 'number', nullable: true },
            expected_loss_kg_per_week: { type: 'number' },
            eta_date: { type: 'string' },
            rationale: { type: 'string' },
          },
          required: ['path','target_kcal','expected_loss_kg_per_week','rationale'],
        },
        alternative: {
          type: 'object',
          nullable: true,
          properties: {
            path: { type: 'string' },
            target_kcal: { type: 'number' },
            extra_activity_kcal_per_day: { type: 'number', nullable: true },
            extra_activity_steps_per_day: { type: 'number', nullable: true },
            expected_loss_kg_per_week: { type: 'number' },
            rationale: { type: 'string' },
          },
          required: ['path','target_kcal','rationale'],
        },
        do_not: { type: 'array', items: { type: 'string' } },
      },
      required: ['primary','do_not'],
    },
  },
  required: [
    'empiricalTDEE','formulaTDEE','deviation_pct','dailyTarget','deficit',
    'safety','protein_g','macros','projection','explanation','recommendation',
  ],
};

// ── Storage helpers ──────────────────────────────────────────────────────
export function loadAITarget() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
}
function saveAITarget(t) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(t)); } catch {}
  try {
    const h = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
    h.push({
      computedAt: t.computedAt,
      dailyTarget: t.dailyTarget,
      empiricalTDEE: t.empiricalTDEE,
      goal_kg_per_week: t.goal_kg_per_week,
    });
    while (h.length > HISTORY_CAP) h.shift();
    localStorage.setItem(HISTORY_KEY, JSON.stringify(h));
  } catch {}
}
function loadPrefs() {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY) || '{}'); } catch { return {}; }
}
function savePrefs(p) { try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch {} }

// ── Data extraction from app state ───────────────────────────────────────
// Build the trailing N-day series of intake (sum of meal kcal/macros) and
// burn (active + basal). Pads with skipped days that have no log entry.
function extractSeries(calorieLog, days) {
  const out = { intake: [], burn: [], pSum: 0, cSum: 0, fSum: 0, nWithIntake: 0 };
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now); d.setDate(now.getDate() - i);
    const key = d.toLocaleDateString('en-CA'); // YYYY-MM-DD local
    const day = calorieLog?.[key] || null;
    if (!day) { continue; }
    const meals = day.meals || {};
    let kcal = 0, p = 0, c = 0, f = 0;
    Object.values(meals).forEach(arr => {
      (arr || []).forEach(e => {
        kcal += e.calories || 0;
        p += e.protein || 0; c += e.carbs || 0; f += e.fat || 0;
      });
    });
    if (kcal > 0) {
      out.intake.push(Math.round(kcal));
      out.pSum += p; out.cSum += c; out.fSum += f; out.nWithIntake++;
    }
    const active = day.caloriesBurned || 0;
    const basal  = day.basalBurned   || 0;
    if (active + basal > 0) out.burn.push(Math.round(active + basal));
  }
  return out;
}

function extractWeightSeries(calorieLog, today, weightLog, days) {
  const todayLog = calorieLog?.[today] || {};
  const ah = todayLog.bodyHistory || { weight: [], bodyFat: [] };
  const manual = (weightLog || []).map(w => ({ date: w.date, value: w.kg }));
  // Merge by date — Apple Health wins on overlap.
  const seen = new Set();
  const merged = [];
  [...(ah.weight || []), ...manual].sort((a,b) => new Date(a.date) - new Date(b.date)).forEach(s => {
    const k = String(s.date).slice(0,10);
    if (seen.has(k)) return;
    seen.add(k); merged.push({ date: k, value: s.value });
  });
  const bfMap = new Map();
  (ah.bodyFat || []).forEach(s => bfMap.set(String(s.date).slice(0,10), s.value));
  const cutoff = Date.now() - days * 86400000;
  const series = merged.filter(s => new Date(s.date).getTime() >= cutoff).map(s => {
    const bf = bfMap.get(s.date) || null;
    const lean = (bf != null) ? +(s.value * (1 - bf/100)).toFixed(2) : null;
    return { date: s.date, weight: +s.value.toFixed(2), bf, lean };
  });
  return series;
}

function mifflinBMR(profile) {
  const age = parseInt(profile.age), h = parseInt(profile.height), w = Math.round(parseFloat(profile.weight));
  if (!age || !h || !w) return null;
  return profile.sex === 'F'
    ? 10*w + 6.25*h - 5*age - 161
    : 10*w + 6.25*h - 5*age + 5;
}
function mifflinTDEE(profile) {
  const bmr = mifflinBMR(profile);
  if (!bmr) return null;
  const mult = parseFloat(profile.activity) || 1.55;
  return Math.round(bmr * mult);
}

// ── Deterministic energetics engine ──────────────────────────────────────
// All TDEE / target arithmetic is done here in JS (reproducible) rather than
// by the LLM. Two independent TDEE estimates are reconciled:
//   1. Energy balance:  meanIntake − weightSlopeKgPerDay × 7700  (gold standard
//      when food logging is reliable; self-corrects for consistent intake bias)
//   2. Measured burn:   mean(active + basal) from Apple Health
// They're blended by the confidence of the weight trend, so a short/noisy
// weight series leans on measured burn and a long clean one leans on balance.
const KCAL_PER_KG = 7700;       // energy density of bodyweight change
const round10 = (n) => Math.round(n / 10) * 10;
const dayIdx = (iso) => Math.floor(new Date(iso + 'T00:00:00').getTime() / 86400000);

function linreg(points) {
  const n = points.length;
  if (n < 2) return null;
  let sx=0, sy=0, sxx=0, sxy=0;
  for (const p of points) { sx+=p.x; sy+=p.y; sxx+=p.x*p.x; sxy+=p.x*p.y; }
  const denom = n*sxx - sx*sx;
  if (denom === 0) return null;
  const slope = (n*sxy - sx*sy) / denom;
  const intercept = (sy - slope*sx) / n;
  const meanY = sy/n;
  let ssTot=0, ssRes=0;
  for (const p of points) { const pred = slope*p.x + intercept; ssRes += (p.y-pred)**2; ssTot += (p.y-meanY)**2; }
  const r2 = ssTot === 0 ? 1 : 1 - ssRes/ssTot;
  return { slope, intercept, n, r2 };
}

// Returns per-calendar-day {date, intake, burn} for the trailing window, plus
// the merged weight/bf series, capped at maxDays. Only real logged values.
function gatherDays(calorieLog, today, weightLog, maxDays) {
  const out = [];
  const now = new Date(today + 'T00:00:00');
  for (let i = maxDays - 1; i >= 0; i--) {
    const d = new Date(now); d.setDate(now.getDate() - i);
    const key = d.toLocaleDateString('en-CA');
    const day = calorieLog?.[key];
    if (!day) continue;
    let kcal=0, p=0, c=0, f=0;
    Object.values(day.meals || {}).forEach(arr => (arr||[]).forEach(e => {
      kcal += e.calories||0; p += e.protein||0; c += e.carbs||0; f += e.fat||0;
    }));
    const burn = (day.caloriesBurned||0) + (day.basalBurned||0);
    out.push({ date: key, intake: kcal>0?Math.round(kcal):null, p, c, f, burn: burn>0?Math.round(burn):null });
  }
  // Weight + body-fat: merge Apple Health bodyHistory (today's snapshot holds
  // the trailing samples) with manual weightLog. Apple Health wins on overlap.
  const todayLog = calorieLog?.[today] || {};
  const ah = todayLog.bodyHistory || { weight: [], bodyFat: [] };
  const seen = new Set();
  const wPts = [];
  [...(ah.weight||[]), ...((weightLog||[]).map(w => ({ date: w.date, value: w.kg })))]
    .sort((a,b) => new Date(a.date) - new Date(b.date))
    .forEach(s => { const k=String(s.date).slice(0,10); if (seen.has(k)||s.value==null) return; seen.add(k); wPts.push({ date:k, value:+(+s.value).toFixed(2) }); });
  const bfMap = new Map();
  (ah.bodyFat||[]).forEach(s => bfMap.set(String(s.date).slice(0,10), s.value));
  const cutoff = dayIdx(today) - maxDays;
  const weight = wPts.filter(s => dayIdx(s.date) >= cutoff).map(s => ({ date:s.date, weight:s.value, bf: bfMap.get(s.date) ?? null }));
  return { days: out, weight };
}

// Median active burn of a logged Riyadah workout over the window. Used to set
// the training-day calorie surcharge (you eat back the workout to hold the
// same deficit). Median is robust to the odd huge/tiny session.
function estimateTrainingBurn(today, maxDays) {
  try {
    const log = JSON.parse(localStorage.getItem('sirat-workouts') || '[]');
    if (!Array.isArray(log)) return { burn: null, count: 0 };
    const cutoff = dayIdx(today) - maxDays;
    const cals = [];
    for (const w of log) {
      if (!w?.timestamp) continue;
      const key = new Date(w.timestamp).toLocaleDateString('en-CA');
      if (dayIdx(key) < cutoff) continue;
      const c = Math.round(w.calories || 0);
      if (c > 0) cals.push(c);
    }
    if (cals.length < 2) return { burn: null, count: cals.length };
    cals.sort((a, b) => a - b);
    return { burn: cals[Math.floor(cals.length / 2)], count: cals.length };
  } catch { return { burn: null, count: 0 }; }
}

// The core estimator. Returns a "computed" block with the authoritative
// numbers the LLM (and the local fallback) must use.
function computeEnergetics({ profile, calorieLog, today, weightLog, lossPerWeek, goalWeightKg, maxDays = 56 }) {
  const { days, weight } = gatherDays(calorieLog, today, weightLog, maxDays);
  const intakeDays = days.filter(d => d.intake != null);
  const burnDays   = days.filter(d => d.burn != null);

  // Smoothed current weight: mean of the last up to 5 readings.
  const recentW = weight.slice(-5);
  const currentWeight = recentW.length ? +(recentW.reduce((a,b)=>a+b.weight,0)/recentW.length).toFixed(2) : (parseFloat(profile.weight)||null);
  const lastBF = [...weight].reverse().find(w => w.bf != null)?.bf ?? null;
  const leanMass = (currentWeight && lastBF!=null) ? +(currentWeight*(1-lastBF/100)).toFixed(2) : null;

  // Weight-trend slope (kg/day) via least squares over all readings in window.
  const wPoints = weight.map(w => ({ x: dayIdx(w.date), y: w.weight }));
  const reg = linreg(wPoints);
  const slopePerDay = reg ? reg.slope : null;             // negative = losing
  const spanDays = wPoints.length >= 2 ? (wPoints[wPoints.length-1].x - wPoints[0].x) : 0;

  // ── Estimate 1: energy balance over the weight-trend span ──
  let tdeeBalance = null, coverage = 0;
  if (reg && spanDays >= 10) {
    const lo = wPoints[0].x, hi = wPoints[wPoints.length-1].x;
    const inSpan = intakeDays.filter(d => { const x=dayIdx(d.date); return x>=lo && x<=hi; });
    coverage = (spanDays+1) > 0 ? inSpan.length / (spanDays+1) : 0;
    if (inSpan.length >= 7 && coverage >= 0.4) {
      const meanIntake = inSpan.reduce((a,b)=>a+b.intake,0) / inSpan.length;
      tdeeBalance = Math.round(meanIntake - slopePerDay * KCAL_PER_KG);
    }
  }

  // ── Estimate 2: measured burn (active + basal) ──
  const tdeeActivity = burnDays.length >= 5
    ? Math.round(burnDays.reduce((a,b)=>a+b.burn,0) / burnDays.length)
    : null;
  const avgIntakeKcal = intakeDays.length
    ? Math.round(intakeDays.reduce((a,b)=>a+b.intake,0) / intakeDays.length) : null;

  // Confidence of the weight-trend estimate (0–1): rewards a long span, many
  // readings, and high logging coverage.
  let trendConf = 0;
  if (tdeeBalance != null) {
    const spanScore = Math.min(1, spanDays/28);
    const ptsScore  = Math.min(1, wPoints.length/8);
    const covScore  = Math.min(1, coverage/0.7);
    trendConf = +(spanScore * ptsScore * covScore).toFixed(2);
  }

  // Reconcile. Lean on the weight trend in proportion to its confidence;
  // otherwise lean on measured burn. Fall back to the Mifflin formula.
  const formulaTDEE = mifflinTDEE({ ...profile, weight: currentWeight }) || 2000;
  let empiricalTDEE, method;
  if (tdeeBalance != null && tdeeActivity != null) {
    const wBal = trendConf;
    empiricalTDEE = Math.round(wBal*tdeeBalance + (1-wBal)*tdeeActivity);
    method = 'balance+burn';
  } else if (tdeeBalance != null) { empiricalTDEE = tdeeBalance; method = 'balance'; }
  else if (tdeeActivity != null)  { empiricalTDEE = tdeeActivity; method = 'measured_burn'; }
  else { empiricalTDEE = formulaTDEE; method = 'formula'; }

  const tdeeDivergencePct = (tdeeBalance!=null && tdeeActivity!=null && tdeeBalance>0)
    ? +(((tdeeActivity - tdeeBalance) / tdeeBalance) * 100).toFixed(1) : null;
  const deviationPct = formulaTDEE > 0 ? +(((empiricalTDEE - formulaTDEE)/formulaTDEE)*100).toFixed(1) : 0;

  const bmr = Math.round(mifflinBMR({ ...profile, weight: currentWeight }) || empiricalTDEE*0.65);
  const dailyDeficit = Math.round((lossPerWeek * KCAL_PER_KG) / 7);
  const requestedTarget = round10(empiricalTDEE - dailyDeficit);
  const observedWeeklyRateKg = slopePerDay != null ? +(slopePerDay*7).toFixed(2) : null;
  // Training-day surcharge: eat back the workout's measured burn so the daily
  // deficit holds on lifting days. Only when we have real workout history.
  const tb = estimateTrainingBurn(today, maxDays);
  const trainingBurn = tb.burn;
  const trainingTarget = trainingBurn != null ? round10(requestedTarget + trainingBurn) : null;

  return {
    method, _sex: profile.sex || 'M',
    empiricalTDEE, tdeeBalance, tdeeActivity, tdee_divergence_pct: tdeeDivergencePct,
    formulaTDEE, deviation_pct: deviationPct,
    bmr, requestedTarget, daily_deficit: dailyDeficit,
    training_burn_kcal: trainingBurn, training_target_kcal: trainingTarget, training_days_seen: tb.count,
    target_loss_kg_per_week: lossPerWeek,
    observed_weekly_rate_kg: observedWeeklyRateKg,
    weight_trend_confidence: trendConf,
    weight_trend_r2: reg ? +reg.r2.toFixed(2) : null,
    span_days: spanDays,
    weight_readings: wPoints.length,
    intake_days: intakeDays.length,
    avg_intake_kcal: avgIntakeKcal,
    burn_days: burnDays.length,
    logging_coverage: +coverage.toFixed(2),
    current_weight_kg: currentWeight,
    body_fat_pct: lastBF,
    lean_mass_kg: leanMass,
    goal_weight_kg: goalWeightKg || null,
    avg_macros: intakeDays.length ? {
      protein_g: +(intakeDays.reduce((a,b)=>a+b.p,0)/intakeDays.length).toFixed(1),
      carbs_g:   +(intakeDays.reduce((a,b)=>a+b.c,0)/intakeDays.length).toFixed(1),
      fat_g:     +(intakeDays.reduce((a,b)=>a+b.f,0)/intakeDays.length).toFixed(1),
    } : { protein_g:0, carbs_g:0, fat_g:0 },
  };
}

function buildPayload({ profile, calorieLog, today, weightLog, goalWeightKg, lossPerWeek, days = 56, current }) {
  const computed = computeEnergetics({ profile, calorieLog, today, weightLog, lossPerWeek, goalWeightKg, maxDays: days });
  const series = extractSeries(calorieLog, days);
  const weightSeries = extractWeightSeries(calorieLog, today, weightLog, days);
  return {
    profile: {
      age: parseInt(profile.age) || null,
      sex: profile.sex || null,
      height_cm: parseInt(profile.height) || null,
    },
    current,
    goal_weight_kg: goalWeightKg || null,
    target_loss_kg_per_week: lossPerWeek,
    // Authoritative numbers — the LLM must use these, not recompute them.
    computed,
    // Raw series for context (pattern-spotting only).
    history: {
      days_available: computed.intake_days,
      daily_intake_kcal: series.intake,
      daily_burn_kcal: series.burn,
      weight_series: weightSeries,
      avg_macros: computed.avg_macros,
    },
    insufficient_history: computed.intake_days < 14,
  };
}

// ── Deterministic record assembly (used for formula mode, Gemini fallback,
//    and as the numeric ground-truth the LLM result is reconciled against) ──
function etaDate(c, ratePerWeek) {
  if (!c.current_weight_kg || !c.goal_weight_kg || !(ratePerWeek > 0)) return '';
  const weeks = Math.max(0, (c.current_weight_kg - c.goal_weight_kg) / ratePerWeek);
  return new Date(Date.now() + weeks * 7 * 86400000).toISOString().slice(0, 10);
}
function macrosFor(targetKcal, c) {
  const w = c.current_weight_kg || 70;
  const lean = c.lean_mass_kg;
  const aggressive = (c.empiricalTDEE - targetKcal) > 0.15 * c.empiricalTDEE;
  const protein_g = Math.round(lean ? lean * (aggressive ? 2.2 : 2.0) : w * (aggressive ? 2.0 : 1.8));
  const fat_g = Math.round(Math.max(0.6 * w, (targetKcal * 0.20) / 9));
  const carbs_g = Math.max(0, Math.round((targetKcal - protein_g * 4 - fat_g * 9) / 4));
  return { protein_g, carbs_g, fat_g };
}
// Re-balance the OTHER macros to hold total kcal ≈ targetKcal after the user
// edits one. Carbs is the default balancer; editing carbs balances via fat.
function rebalanceMacros(prev, key, val, targetKcal) {
  const m = { ...prev, [key]: Math.max(0, Math.round(val) || 0) };
  if (key === 'carbs_g') {
    m.fat_g = Math.max(0, Math.round((targetKcal - m.protein_g * 4 - m.carbs_g * 4) / 9));
  } else {
    // editing protein or fat → carbs absorbs the remainder
    m.carbs_g = Math.max(0, Math.round((targetKcal - m.protein_g * 4 - m.fat_g * 9) / 4));
  }
  return m;
}
function explanationText(c) {
  let head;
  if (c.method === 'balance+burn') head = `TDEE ${c.empiricalTDEE} kcal — energy-balance estimate (${c.tdeeBalance}) blended with measured Apple Health burn (${c.tdeeActivity}) at ${Math.round(c.weight_trend_confidence*100)}% weight-trend confidence.`;
  else if (c.method === 'balance') head = `TDEE ${c.empiricalTDEE} kcal — back-solved from avg intake minus your measured ${c.observed_weekly_rate_kg} kg/wk weight trend over ${c.span_days} days (${c.weight_readings} weigh-ins).`;
  else if (c.method === 'measured_burn') head = `TDEE ${c.empiricalTDEE} kcal — averaged from ${c.burn_days} days of measured Apple Health burn (not enough weight history yet for the trend method).`;
  else head = `TDEE ${c.empiricalTDEE} kcal — Mifflin-St Jeor formula estimate (insufficient logged data for a personalised number).`;
  return `${head} Daily target = TDEE − ${c.daily_deficit} kcal for ${c.target_loss_kg_per_week} kg/wk of scale weight.`;
}
function localDoNot(c) {
  const dn = [];
  if (c.bmr) dn.push(`Don't eat below ${c.bmr} kcal (your BMR) — it stalls metabolism and burns muscle, not just fat.`);
  if (c.lean_mass_kg) dn.push(`Don't let protein fall below ${Math.round(c.lean_mass_kg * 1.8)} g/day — it preserves lean mass in a deficit.`);
  if (c.weight_trend_confidence < 0.4) dn.push(`Don't over-trust this number yet — only ${c.weight_readings} weigh-ins logged; weigh in daily for ~2 weeks to sharpen it.`);
  else if (c.tdee_divergence_pct != null && Math.abs(c.tdee_divergence_pct) > 15) dn.push(`Don't assume the device burn is exact — it disagrees with your intake+weight maths by ${Math.round(Math.abs(c.tdee_divergence_pct))}%.`);
  return dn.slice(0, 3);
}

// Build a complete record from the deterministic "computed" block. This is the
// numeric source of truth; the LLM only refines narrative/macros on top.
function assembleLocalRecord(c, lossPerWeek) {
  const tdee = c.empiricalTDEE;
  const requested = c.requestedTarget;
  const deficit = tdee - requested;
  const deficitPct = tdee > 0 ? deficit / tdee : 0;
  const sex = (typeof c._sex === 'string') ? c._sex : 'M';
  const bfLow = c.body_fat_pct != null && c.body_fat_pct < (sex === 'F' ? 20 : 12);

  const flags = [];
  let verdict = 'safe';
  if (requested < c.bmr) { verdict = 'not_recommended'; flags.push(`Requested target ${requested} kcal is below your BMR (${c.bmr} kcal).`); }
  if (deficitPct > 0.25) { verdict = 'not_recommended'; flags.push(`Deficit is ${Math.round(deficitPct*100)}% of TDEE — over the 25% safe ceiling.`); }
  else if (deficitPct >= 0.20 && verdict === 'safe') { verdict = 'caution'; flags.push(`Deficit is ${Math.round(deficitPct*100)}% of TDEE — aggressive; keep protein high.`); }
  if (bfLow && lossPerWeek === 1) { verdict = 'not_recommended'; flags.push('Body fat already low — 1 kg/wk risks lean mass.'); }
  if (c.tdee_divergence_pct != null && Math.abs(c.tdee_divergence_pct) > 15) {
    if (verdict === 'safe') verdict = 'caution';
    flags.push(`Energy-balance and measured-burn TDEE differ by ${Math.round(Math.abs(c.tdee_divergence_pct))}% — treat the target as approximate.`);
  }
  if (c.weight_trend_confidence < 0.4 && c.method !== 'formula') flags.push('Short/sparse weight history — the estimate sharpens as you log more weigh-ins.');
  if (!flags.length) flags.push(`Sustainable — ${Math.round(deficitPct*100)}% deficit, comfortably above BMR.`);

  let primary, alternative = null;
  if (verdict === 'safe') {
    const loss = +(deficit * 7 / KCAL_PER_KG).toFixed(2);
    primary = { path: 'proceed', target_kcal: requested, expected_loss_kg_per_week: loss, eta_date: etaDate(c, loss),
      rationale: `Eat ${requested} kcal — a ${Math.round(deficitPct*100)}% deficit off your ${tdee} kcal TDEE, ~${loss} kg/wk, sits safely above BMR.` };
  } else {
    const eatTarget = round10(Math.max(c.bmr, tdee - 0.20 * tdee));
    const eatLoss = +((tdee - eatTarget) * 7 / KCAL_PER_KG).toFixed(2);
    primary = { path: 'eat_more', target_kcal: eatTarget, expected_loss_kg_per_week: eatLoss, eta_date: etaDate(c, eatLoss),
      rationale: `Eat ${eatTarget} kcal — keeps you above BMR (${c.bmr}) at a ${Math.round((tdee-eatTarget)/tdee*100)}% deficit, ~${eatLoss} kg/wk, protecting lean mass.` };
    const extraKcal = Math.min(400, Math.max(50, Math.round(deficit - (tdee - eatTarget))));
    const moveTarget = Math.max(c.bmr + 50, requested);
    alternative = { path: 'move_more', target_kcal: moveTarget, extra_activity_kcal_per_day: extraKcal, extra_activity_steps_per_day: extraKcal * 20, expected_loss_kg_per_week: lossPerWeek,
      rationale: `Or hold ${moveTarget} kcal and add ~${extraKcal} kcal/day activity (~${(extraKcal*20).toLocaleString()} steps) to reach ${lossPerWeek} kg/wk without cutting further.` };
  }

  const target = primary.target_kcal;
  const macros = macrosFor(target, c);
  const weeks = (c.current_weight_kg && c.goal_weight_kg && primary.expected_loss_kg_per_week > 0)
    ? Math.max(0, Math.ceil((c.current_weight_kg - c.goal_weight_kg) / primary.expected_loss_kg_per_week)) : 0;
  return {
    empiricalTDEE: tdee, formulaTDEE: c.formulaTDEE, deviation_pct: c.deviation_pct, deviation_note: null,
    dailyTarget: target, deficit: tdee - target,
    safety: { verdict, flags, recommendedAlternative: alternative ? alternative.path : null },
    protein_g: macros.protein_g, macros,
    projection: { weeks_to_goal: weeks, goal_date: weeks ? new Date(Date.now() + weeks * 7 * 86400000).toISOString().slice(0, 10) : '' },
    explanation: explanationText(c),
    recommendation: { primary, alternative, do_not: localDoNot(c) },
    _computed: c,
  };
}

// Overwrite the LLM's numeric fields with the deterministic ones so the
// headline TDEE/target are always reproducible; keep its narrative/macros.
function reconcileWithComputed(parsed, c) {
  parsed.empiricalTDEE = c.empiricalTDEE;
  parsed.formulaTDEE   = c.formulaTDEE;
  parsed.deviation_pct = c.deviation_pct;
  if (parsed.recommendation?.primary) {
    let t = Math.round(parsed.recommendation.primary.target_kcal || c.requestedTarget);
    if (t < c.bmr) t = c.bmr;
    parsed.recommendation.primary.target_kcal = t;
    parsed.dailyTarget = t;
  } else if ((parsed.dailyTarget || 0) < c.bmr) {
    parsed.dailyTarget = c.bmr;
  }
  parsed.deficit = c.empiricalTDEE - (parsed.dailyTarget || c.requestedTarget);
  parsed._computed = c;
  return parsed;
}

// Training-day override derived from a rest-day target + the measured workout
// burn. Protein/fat held; the extra calories go to carbs (training fuel).
function trainingFromGoals(cal, prot, carb, fat, burn) {
  if (!burn || burn <= 0) return null;
  return {
    calories: Math.round(cal + burn),
    protein:  Math.round(prot),
    carbs:    Math.round(carb + burn / 4),
    fat:      Math.round(fat),
  };
}
// Merge a rest-day target into goalsRaw, and when we have a workout-burn
// estimate, also write the training-day override and enable it so the ring /
// Log show the higher number automatically on logged workout days.
function goalsWithTarget(goalsRaw, cal, prot, carb, fat, burn) {
  const base = { ...goalsRaw, calories: Math.round(cal), protein: Math.round(prot), carbs: Math.round(carb), fat: Math.round(fat) };
  const training = trainingFromGoals(cal, prot, carb, fat, burn);
  return training ? { ...base, training, trainingEnabled: true } : base;
}

// ── Live projection (the interactive lever) ──────────────────────────────
// Given a candidate daily intake and the user's measured TDEE, what happens to
// their weight. Pure energy balance — no AI call — so the slider updates live.
function projectIntake(intake, c) {
  const tdee = c?.empiricalTDEE || 2000;
  const bmr  = c?.bmr || Math.round(tdee * 0.65);
  const rateKgWk = +(((intake - tdee) * 7) / KCAL_PER_KG).toFixed(2); // <0 lose, >0 gain
  const deficit = tdee - intake;
  const deficitPct = tdee > 0 ? deficit / tdee : 0;
  let verdict = 'safe', msg;
  if (intake < bmr) { verdict = 'danger'; msg = `Below your BMR (${bmr} kcal) — burns muscle, not just fat. Unsustainable.`; }
  else if (deficitPct > 0.25) { verdict = 'danger'; msg = `${Math.round(deficitPct*100)}% deficit — too aggressive; you'll shed lean mass.`; }
  else if (deficitPct >= 0.20) { verdict = 'caution'; msg = `${Math.round(deficitPct*100)}% deficit — aggressive. Hold protein high.`; }
  else if (rateKgWk > 0.04) { verdict = 'caution'; msg = `Above your maintenance — you'll gain ~${Math.abs(rateKgWk)} kg/wk.`; }
  else if (Math.abs(rateKgWk) <= 0.04) { verdict = 'safe'; msg = `Right around maintenance — your weight holds steady.`; }
  else { verdict = 'safe'; msg = `Sustainable — ${Math.round(deficitPct*100)}% deficit, comfortably above BMR.`; }
  let etaWeeks = null, eta = null;
  if (c?.current_weight_kg && c?.goal_weight_kg && rateKgWk < -0.02 && c.current_weight_kg > c.goal_weight_kg) {
    etaWeeks = Math.ceil((c.current_weight_kg - c.goal_weight_kg) / Math.abs(rateKgWk));
    eta = new Date(Date.now() + etaWeeks * 7 * 86400000).toISOString().slice(0, 10);
  }
  return { rateKgWk, deficitPct, verdict, msg, etaWeeks, eta, tdee, bmr };
}
function loadPrediction() { try { return JSON.parse(localStorage.getItem(PREDICTION_KEY) || 'null'); } catch { return null; } }
function savePrediction(p) { try { localStorage.setItem(PREDICTION_KEY, JSON.stringify(p)); } catch {} }
// Plain-English direction phrase for a weekly rate.
function ratePhrase(r) {
  if (r <= -0.02) return `lose ~${Math.abs(r)} kg/wk`;
  if (r >= 0.02)  return `gain ~${r} kg/wk`;
  return 'hold steady';
}

// ── UI helpers ───────────────────────────────────────────────────────────
const verdictTone = {
  safe:            { label: 'Safe',            emoji: '🟢', color: '#00c9a7' },
  caution:         { label: 'Caution',         emoji: '🟡', color: '#f0a86b' },
  not_recommended: { label: 'Not recommended', emoji: '🔴', color: '#ff6b6b' },
};

function fmtKcal(n) { return Math.round(n || 0).toLocaleString(); }
function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { day:'numeric', month:'short', year:'numeric' }); } catch { return iso; }
}

// ── Component ────────────────────────────────────────────────────────────
export default function AICalorieTarget({
  colors,
  calorieLog,
  today,
  weightLog,
  wizardProfile,
  goalsRaw,
  saveGoals,
  showToast,
  forceExpand,
  onConsumeForceExpand,
}) {
  const { text, muted, border, s1, s2, gold, teal, accent, danger, darkMode } = colors;

  const [stored, setStored] = useState(loadAITarget);
  const [prefs, setPrefsState] = useState(loadPrefs);
  const lossPerWeek = prefs.lossPerWeek === 1 ? 1 : 0.5;
  const burnSource  = prefs.burnSource || 'apple_health';
  const setPrefs = (p) => { savePrefs(p); setPrefsState(p); };

  // Always start expanded — previously it auto-collapsed once a result was
  // stored, hiding the target/macros the user came to see.
  const [expanded, setExpanded] = useState(true);
  // Editable macro override at the explored target. null = use the computed
  // split; an object = the user's amended P/C/F. Resets when the target or a
  // fresh computation changes.
  const [macroEdit, setMacroEdit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [explanationOpen, setExplanationOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  // The interactive target the user is exploring on the slider. Resets to the
  // recommended target whenever a fresh result lands.
  const [sliderKcal, setSliderKcal] = useState(null);
  const [kcalText, setKcalText] = useState(null); // raw text while typing the exact kcal; null = follow the slider
  useEffect(() => {
    // Open on the value actually in the log (the applied ring goal), falling
    // back to the AI recommendation if no goal is set yet.
    const inLog = Math.round(goalsRaw?.calories || stored?.dailyTarget || 0);
    if (inLog) setSliderKcal(inLog);
  }, [stored?.computedAt, goalsRaw?.calories]); // eslint-disable-line react-hooks/exhaustive-deps
  // Drop any macro edit when the target moves or a fresh result lands, so the
  // split re-derives from the new kcal.
  useEffect(() => { setMacroEdit(null); }, [sliderKcal, stored?.computedAt]);

  useEffect(() => {
    if (forceExpand) {
      setExpanded(true);
      setExplanationOpen(true);
      onConsumeForceExpand && onConsumeForceExpand();
      try {
        setTimeout(() => {
          const el = document.getElementById('ai-cal-target-card');
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior:'smooth', block:'start' });
        }, 60);
      } catch {}
    }
  }, [forceExpand, onConsumeForceExpand]);

  // Derive "current" snapshot from today's body history
  const current = useMemo(() => {
    const todayLog = calorieLog?.[today] || {};
    const bh = todayLog.bodyHistory || { weight: [], bodyFat: [] };
    const lastW = bh.weight?.[bh.weight.length - 1];
    const lastBF = bh.bodyFat?.[bh.bodyFat.length - 1];
    const manualLast = (weightLog || []).slice(-1)[0];
    const weight_kg = lastW?.value || manualLast?.kg || parseFloat(wizardProfile?.weight) || null;
    const body_fat_pct = lastBF?.value || null;
    const lean = (weight_kg && body_fat_pct) ? +(weight_kg * (1 - body_fat_pct/100)).toFixed(2) : null;
    const h = parseFloat(wizardProfile?.height) || 0;
    const bmi = (weight_kg && h) ? +(weight_kg / Math.pow(h/100, 2)).toFixed(2) : null;
    return { weight_kg, body_fat_pct, lean_mass_kg: lean, bmi };
  }, [calorieLog, today, weightLog, wizardProfile]);

  const compute = useCallback(async (opts = {}) => {
    setLoading(true); setError(null);
    const goalWeightKg = parseFloat(prefs.goalWeightKg) || null; // optional override
    const profile = {
      age: wizardProfile?.age,
      sex: wizardProfile?.sex,
      height: wizardProfile?.height,
      weight: current.weight_kg,
      activity: wizardProfile?.activity || '1.55',
    };
    const payload = buildPayload({
      profile, calorieLog, today, weightLog,
      goalWeightKg, lossPerWeek, days: opts.days || 56, current,
    });
    try {
      // Feed the live "what the AI is using" rows with the real computed
      // data + the computation chain (intake → trend → TDEE → deficit → target).
      publishSources('aiCalorieTarget', calorieTargetSources(payload.computed, lossPerWeek));
      if (burnSource === 'formula' || !payload.history.days_available) {
        return finishCompute(assembleLocalRecord(payload.computed, lossPerWeek), payload);
      }
      const raw = await callGemini(JSON.stringify(payload), {
        systemInstruction: SYSTEM_PROMPT,
        responseSchema: RESPONSE_SCHEMA,
        model: 'gemini-2.5-pro',
        maxOutputTokens: 8192,
        temperature: 0.3,
        feature: 'aiCalorieTarget',
      });
      const parsed = reconcileWithComputed(JSON.parse(raw), payload.computed);
      finishCompute(parsed, payload);
    } catch (e) {
      console.warn('[AICalorieTarget] compute failed, using local estimate:', e);
      // We still have a fully deterministic result — use it rather than erroring.
      try { return finishCompute(assembleLocalRecord(payload.computed, lossPerWeek), payload); }
      catch (e2) { setError(e.message || 'Compute failed'); setLoading(false); }
    }
  }, [wizardProfile, current, calorieLog, today, weightLog, lossPerWeek, burnSource, prefs.goalWeightKg]);

  function finishCompute(result, payload) {
    const prev = loadAITarget();
    const c = result._computed || payload?.computed;
    const record = {
      ...result,
      computedAt: new Date().toISOString(),
      goal_kg_per_week: lossPerWeek,
      burn_source: burnSource,
      days_used: payload.history.days_available,
    };
    // ── Learning loop ──
    // (1) TDEE revision: did the measured maintenance move since last time?
    if (prev?.empiricalTDEE && Math.abs(prev.empiricalTDEE - record.empiricalTDEE) >= 20) {
      record.tdeeRevisedFrom = Math.round(prev.empiricalTDEE);
    }
    // (2) Prediction vs reality: compare the last applied prediction to the
    //     weight trend we now actually observe.
    const prior = loadPrediction();
    if (prior && c?.observed_weekly_rate_kg != null) {
      record.predictionCheck = {
        predicted: prior.predictedRateKgWk, actual: c.observed_weekly_rate_kg,
        intake: prior.intake, at: prior.at,
      };
    }
    saveAITarget(record);
    setStored(record);
    setLoading(false);

    // Only auto-apply when the verdict is safe or caution. not_recommended
    // means the model itself says don't eat at this target — surface it but
    // require explicit user confirmation via the Recommendation card.
    const verdict = record.safety?.verdict || 'safe';
    if (verdict !== 'not_recommended' && saveGoals && goalsRaw) {
      saveGoals(goalsWithTarget(goalsRaw, record.dailyTarget, record.macros.protein_g, record.macros.carbs_g, record.macros.fat_g, c?.training_burn_kcal));
    }
    // Stamp the prediction for the applied target so next compute can grade it.
    if (verdict !== 'not_recommended' && c) {
      savePrediction({ at: record.computedAt, intake: record.dailyTarget, predictedRateKgWk: projectIntake(record.dailyTarget, c).rateKgWk, tdee: record.empiricalTDEE });
    }
    if (verdict === 'not_recommended') {
      showToast && showToast(`Target needs review — see recommendation`);
    } else if (prev && Math.abs((prev.dailyTarget || 0) - record.dailyTarget) > 50) {
      const dir = record.dailyTarget > prev.dailyTarget ? '+' : '−';
      showToast && showToast(`Target updated: ${fmtKcal(record.dailyTarget)} kcal (${dir}${Math.abs(Math.round(record.dailyTarget - prev.dailyTarget))})`);
    } else {
      showToast && showToast(`Target set: ${fmtKcal(record.dailyTarget)} kcal`);
    }
  }

  // Apply a recommendation path to the calorie ring. Recomputes macros to
  // match the new target kcal (preserves protein floor, scales c+f).
  function applyRecommendation(rec) {
    if (!rec || !saveGoals || !goalsRaw || !stored) return;
    const tgt = Math.round(rec.target_kcal);
    const proteinFloor = Math.round(stored.protein_g || stored.macros.protein_g || 0);
    const proteinKcal = proteinFloor * 4;
    const fatKcal = Math.round((stored.macros.fat_g || 0) * 9);
    let carbsKcal = tgt - proteinKcal - fatKcal;
    if (carbsKcal < 0) carbsKcal = Math.max(0, tgt - proteinKcal - Math.max(40 * 9, Math.round(fatKcal * 0.6)));
    const burn = stored._computed?.training_burn_kcal;
    saveGoals(goalsWithTarget(goalsRaw, tgt, proteinFloor, Math.max(0, Math.round(carbsKcal / 4)), Math.round(fatKcal / 9), burn));
    showToast && showToast(`Applied ${fmtKcal(tgt)} kcal — ${rec.path.replace('_',' ')}`);
  }

  // Apply an arbitrary explored target from the slider/input (the override).
  // Macros re-derive (protein/fat floors held, carbs take the remainder) and we
  // log the prediction so the learning loop can grade it later.
  function applyTarget(kcal, macrosOverride) {
    if (!saveGoals || !goalsRaw || !stored?._computed) return;
    const c = stored._computed;
    const tgt = Math.round(kcal);
    const m = macrosOverride || macrosFor(tgt, c);
    saveGoals(goalsWithTarget(goalsRaw, tgt, m.protein_g, m.carbs_g, m.fat_g, c.training_burn_kcal));
    const proj = projectIntake(tgt, c);
    savePrediction({ at: new Date().toISOString(), intake: tgt, predictedRateKgWk: proj.rateKgWk, tdee: c.empiricalTDEE });
    showToast && showToast(`Applied ${fmtKcal(tgt)} kcal · ${ratePhrase(proj.rateKgWk)}`);
  }

  // ── Styles ────────────────────────────────────────────────────────────
  const card = {
    borderRadius: 14, border: `1px solid ${border}`, background: s1,
    marginBottom: 12, overflow: 'hidden',
  };
  const headerRow = {
    width:'100%', padding:'13px 16px', background:'transparent', border:'none',
    color: text, cursor:'pointer', fontFamily:'inherit',
    display:'flex', justifyContent:'space-between', alignItems:'center', gap: 10,
  };
  const sectionLabel = { fontSize: 10, color: muted, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 6 };
  const radioRow = { display:'flex', gap:6, flexWrap:'wrap' };
  const chip = (active, c=gold) => ({
    padding:'6px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700,
    border: `1px solid ${active ? c : border}`,
    background: active ? c + '22' : 'transparent',
    color: active ? c : muted,
    cursor: 'pointer', fontFamily: 'inherit',
  });
  const statRow = { display:'flex', justifyContent:'space-between', alignItems:'baseline', padding:'8px 0', borderBottom:`1px solid ${border}` };

  const verdict = stored?.safety?.verdict || 'safe';
  const tone = verdictTone[verdict] || verdictTone.safe;

  return (
    <div id="ai-cal-target-card" style={card}>
      {/* Header */}
      <button onClick={() => setExpanded(o => !o)} style={headerRow}>
        <span style={{ display:'flex', alignItems:'center', gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 800 }}>🎯 AI Calorie Target</span>
          {stored && !expanded && (
            <span style={{ fontSize: 12, color: gold, fontWeight: 800, fontVariantNumeric:'tabular-nums', whiteSpace:'nowrap' }}>
              · {fmtKcal(stored.dailyTarget)} kcal
            </span>
          )}
        </span>
        <span style={{ fontSize: 13, color: muted }}>{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div style={{ padding: '0 14px 14px', display:'flex', flexDirection:'column', gap: 14 }}>
          {/* Options — rate / burn source / goal weight. Collapsed by default
              so the slider + projection lead. These set the AI's recommended
              default; the slider lets you override to any intake afterwards. */}
          <div>
            <button onClick={() => setOptionsOpen(o => !o)}
                    style={{ background:'transparent', border:'none', color: muted, fontSize: 11, fontWeight: 800, letterSpacing:'0.1em', textTransform:'uppercase', textAlign:'left', padding: 0, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6 }}>
              {optionsOpen ? '▾' : '▸'} Options · {lossPerWeek} kg/wk · {{ apple_health:'Apple Health', manual:'Logged data', formula:'Formula' }[burnSource]}{prefs.goalWeightKg ? ` · goal ${prefs.goalWeightKg}kg` : ''}
            </button>
            {optionsOpen && (
              <div style={{ display:'flex', flexDirection:'column', gap: 12, marginTop: 10 }}>
                <div>
                  <div style={sectionLabel}>Weekly weight loss target</div>
                  <div style={radioRow}>
                    {[{ v: 0.5, l: '0.5 kg / week' }, { v: 1.0, l: '1.0 kg / week' }].map(g => (
                      <button key={g.v} style={chip(lossPerWeek === g.v, gold)} onClick={() => setPrefs({ ...prefs, lossPerWeek: g.v })}>{g.l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={sectionLabel}>Burn data source</div>
                  <div style={radioRow}>
                    {[{ v: 'apple_health', l: 'Apple Health' }, { v: 'manual', l: 'Logged data' }, { v: 'formula', l: 'Formula only' }].map(b => (
                      <button key={b.v} style={chip(burnSource === b.v, teal)} onClick={() => setPrefs({ ...prefs, burnSource: b.v })}>{b.l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={sectionLabel}>Goal weight (kg, optional)</div>
                  <input type="number" inputMode="decimal" placeholder="—"
                         value={prefs.goalWeightKg || ''}
                         onChange={e => setPrefs({ ...prefs, goalWeightKg: e.target.value })}
                         style={{ width: 120, padding:'8px 10px', borderRadius: 9, border:`1px solid ${border}`, background: s2, color: text, fontSize: 13, fontWeight: 700, textAlign:'center', fontFamily:'inherit', outline:'none' }}/>
                </div>
              </div>
            )}
          </div>

          {/* Compute — while running, show the live pipeline animation with
              the actual data + computations streaming in (publishSources). */}
          {loading ? (
            <AIAnalysingPipeline
              label="Computing target"
              computeLabel="Solving energy balance"
              feature="aiCalorieTarget"
              accent="teal"
              sources={["Intake history", "Weight trend", "Measured burn", "Reconciling TDEE", "Deficit + macros"]}
              theme={{ teal, ink3: muted, ink4: muted, lineSoft: border, line: border }}
            />
          ) : (
            <button onClick={() => compute()}
                    style={{ width:'100%', padding: 12, background: gold, border:'none', borderRadius: 12, color: '#000', fontSize: 13, fontWeight: 900, cursor: 'pointer', fontFamily:'inherit' }}>
              {stored ? 'Recompute Now' : 'Compute My Target'}
            </button>
          )}

          {error && (
            <div style={{ padding:'8px 10px', background:'rgba(255,107,107,0.10)', border:`1px solid ${danger}55`, borderRadius: 8, color: danger, fontSize: 12 }}>
              Couldn't compute target right now. {error}
              <div style={{ marginTop: 6 }}>
                <button onClick={() => { setError(null); compute(); }} style={chip(true, danger)}>Retry</button>
                <button onClick={() => {
                  const profile = { age: wizardProfile?.age, sex: wizardProfile?.sex, height: wizardProfile?.height, weight: current.weight_kg, activity: wizardProfile?.activity || '1.55' };
                  const computed = computeEnergetics({ profile, calorieLog, today, weightLog, lossPerWeek, goalWeightKg: parseFloat(prefs.goalWeightKg) || null });
                  finishCompute(assembleLocalRecord(computed, lossPerWeek), { history: { days_available: computed.intake_days }, computed });
                  setError(null);
                }} style={{ ...chip(false, muted), marginLeft: 6 }}>Use local estimate</button>
              </div>
            </div>
          )}

          {/* Result — redesigned: story → lever → projection → coaching → learning */}
          {stored && stored._computed && (() => {
            const c = stored._computed;
            const sk = sliderKcal != null ? sliderKcal : Math.round(stored.dailyTarget);
            const proj = projectIntake(sk, c);
            const projTone = { safe: teal, caution: '#f0a86b', danger: danger };
            const pc = projTone[proj.verdict];
            const applied = Math.abs((goalsRaw?.calories || 0) - sk) <= 5;
            const sMin = Math.max(1000, round10(c.bmr - 250));
            const sMax = round10(c.empiricalTDEE + 700);
            const posOf = (v) => Math.max(2, Math.min(98, ((v - sMin) / (sMax - sMin)) * 100));
            const trendColor = (c.observed_weekly_rate_kg || 0) < -0.02 ? teal : (c.observed_weekly_rate_kg || 0) > 0.02 ? '#f0a86b' : muted;
            const baseM = macrosFor(sk, c);
            const m = macroEdit || baseM;
            const macroKcal = m.protein_g * 4 + m.carbs_g * 4 + m.fat_g * 9;
            const alt = stored.recommendation?.alternative;
            const dn = stored.recommendation?.do_not || [];
            return (
            <div style={{ borderTop: `1px solid ${border}`, paddingTop: 12, display:'flex', flexDirection:'column', gap: 14 }}>

              {/* 1 · WHAT'S HAPPENING NOW — the measured reality */}
              <div style={{ padding:'12px', borderRadius: 12, background: s2, border:`1px solid ${border}` }}>
                <div style={sectionLabel}>What's happening now</div>
                <div style={{ display:'flex', alignItems:'baseline', gap: 7, flexWrap:'wrap', fontSize: 13, color: text, fontWeight: 700 }}>
                  <span>burn <b style={{ color: gold }}>{fmtKcal(c.empiricalTDEE)}</b></span>
                  <span style={{ color: muted }}>−</span>
                  <span>eat <b style={{ color: gold }}>{c.avg_intake_kcal ? fmtKcal(c.avg_intake_kcal) : '—'}</b></span>
                  <span style={{ color: muted }}>=</span>
                  <span style={{ color: trendColor }}>{c.observed_weekly_rate_kg != null ? ratePhrase(c.observed_weekly_rate_kg) : 'trend forming'}</span>
                </div>
                <div style={{ fontSize: 10, color: muted, marginTop: 7, fontWeight: 600, letterSpacing:'0.04em' }}>
                  measured · {c.burn_days || 0}d burn · {c.weight_readings || 0} weigh-in{c.weight_readings === 1 ? '' : 's'}{(c.weight_trend_confidence || 0) < 0.4 ? ' · ⚠ low confidence' : ''}
                </div>
              </div>

              {/* 2 · YOUR TARGET — the interactive lever (override + live projection) */}
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                  <div style={sectionLabel}>Your target — drag to explore</div>
                  {sk !== Math.round(stored.dailyTarget) && (
                    <button onClick={() => setSliderKcal(Math.round(stored.dailyTarget))}
                            style={{ background:'transparent', border:'none', color: muted, fontSize: 10.5, fontWeight: 700, cursor:'pointer', fontFamily:'inherit', textDecoration:'underline' }}>
                      reset to {fmtKcal(stored.dailyTarget)}
                    </button>
                  )}
                </div>
                <input type="range" min={sMin} max={sMax} step={10} value={sk}
                       onChange={e => { setKcalText(null); setSliderKcal(parseInt(e.target.value)); }}
                       style={{ width:'100%', accentColor: pc, marginTop: 4, touchAction:'none' }} />
                {/* Tick markers — stagger labels onto a second row when two sit
                    within 16% of each other so BMR/rec don't overlap. */}
                <div style={{ position:'relative', height: 28, marginBottom: 6 }}>
                  {(() => {
                    const marks = [{ v: c.bmr, l: 'BMR' }, { v: Math.round(stored.dailyTarget), l: 'rec' }, { v: c.empiricalTDEE, l: 'maintain' }]
                      .map(t => ({ ...t, pos: posOf(t.v) }))
                      .sort((a, b) => a.pos - b.pos);
                    let lastPos = -99, lastRow = 1;
                    return marks.map(t => {
                      const row = (t.pos - lastPos) < 16 ? (1 - lastRow) : 0;
                      lastPos = t.pos; lastRow = row;
                      return (
                        <div key={t.l} style={{ position:'absolute', left:`${t.pos}%`, transform:'translateX(-50%)', textAlign:'center', top: 0 }}>
                          <div style={{ width:1, height:5, background: muted, margin:'0 auto 2px' }} />
                          <div style={{ fontSize: 8, color: muted, fontWeight: 700, letterSpacing:'0.5px', whiteSpace:'nowrap', marginTop: row * 10 }}>{t.l}</div>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* live projection */}
                <div style={{ padding:'12px', borderRadius: 12, background: pc + '14', border:`1px solid ${pc}55` }}>
                  <div style={{ display:'flex', alignItems:'baseline', gap: 8, flexWrap:'wrap' }}>
                    <span style={{ fontSize: 24, fontWeight: 800, color: pc, fontVariantNumeric:'tabular-nums', letterSpacing:'-0.5px' }}>{fmtKcal(sk)}</span>
                    <span style={{ fontSize: 11, color: muted, fontWeight: 700 }}>kcal/day</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: text }}>→ {ratePhrase(proj.rateKgWk)}</span>
                  </div>
                  {proj.eta && (
                    <div style={{ fontSize: 11.5, color: muted, fontWeight: 700, marginTop: 3 }}>goal weight by {fmtDate(proj.eta)} · {proj.etaWeeks} wks</div>
                  )}
                  <div style={{ fontSize: 12, color: text, lineHeight: 1.5, marginTop: 7 }}>{proj.msg}</div>
                </div>

                {/* apply + exact input */}
                <div style={{ display:'flex', gap: 8, alignItems:'center', marginTop: 10 }}>
                  <button onClick={() => applyTarget(sk, m)} disabled={applied}
                          style={{ flex: 1, padding: 11, background: applied ? s2 : teal, border:'none', borderRadius: 10, color: applied ? muted : '#000', fontSize: 13, fontWeight: 900, cursor: applied ? 'default' : 'pointer', fontFamily:'inherit' }}>
                    {applied ? 'Applied to your ring ✓' : `Apply ${fmtKcal(sk)} kcal`}
                  </button>
                  <input type="text" inputMode="numeric" value={kcalText != null ? kcalText : String(sk)}
                         onFocus={e => e.target.select()}
                         onChange={e => { const t = e.target.value.replace(/[^0-9]/g, ''); setKcalText(t); const v = parseInt(t); if (!isNaN(v)) setSliderKcal(v); }}
                         onBlur={() => { let v = parseInt(kcalText); if (isNaN(v)) v = sk; v = Math.max(800, Math.min(6000, v)); setSliderKcal(v); setKcalText(null); }}
                         style={{ width: 82, padding:'10px 8px', borderRadius: 9, border:`1px solid ${border}`, background: s2, color: text, fontSize: 13, fontWeight: 800, textAlign:'center', fontFamily:'inherit', outline:'none' }} />
                </div>
              </div>

              {/* 3 · Macros (editable) + training-day, at the explored target */}
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 6 }}>
                  <span style={{ color: muted, fontSize: 12, fontWeight: 700 }}>Macros at {fmtKcal(sk)}</span>
                  <span style={{ color: Math.abs(macroKcal - sk) <= 50 ? muted : '#f0a86b', fontSize: 10.5, fontWeight: 700, display:'flex', alignItems:'center', gap: 6 }}>
                    ≈ {fmtKcal(macroKcal)} kcal{macroEdit ? ' · edited' : ''}
                    {macroEdit && (
                      <button onClick={() => setMacroEdit(null)}
                              style={{ background:'transparent', border:'none', color: muted, fontSize: 10.5, fontWeight: 700, cursor:'pointer', fontFamily:'inherit', textDecoration:'underline', padding: 0 }}>reset</button>
                    )}
                  </span>
                </div>
                <div style={{ display:'flex', gap: 8 }}>
                  {[{ k:'protein_g', l:'Protein', step:5, c:teal }, { k:'carbs_g', l:'Carbs', step:5, c:gold }, { k:'fat_g', l:'Fat', step:2, c:accent }].map(mac => (
                    <div key={mac.k} style={{ flex:1, background:s2, border:`1px solid ${border}`, borderRadius:10, padding:'8px 6px' }}>
                      <div style={{ fontSize:9, color:mac.c, fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase', textAlign:'center', marginBottom:5 }}>{mac.l}</div>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:2 }}>
                        <button aria-label={`Decrease ${mac.l}`} onClick={() => setMacroEdit(rebalanceMacros(m, mac.k, (m[mac.k]||0) - mac.step, sk))}
                                style={{ width:26, height:26, borderRadius:7, background:s1, border:`1px solid ${border}`, color:text, fontSize:15, lineHeight:1, cursor:'pointer', fontFamily:'inherit' }}>−</button>
                        <span style={{ display:'inline-flex', alignItems:'baseline' }}>
                          <input aria-label={`${mac.l} grams`} type="text" inputMode="numeric" value={String(m[mac.k] ?? 0)}
                                 onFocus={e => e.target.select()}
                                 onChange={e => { const v = parseInt(e.target.value.replace(/[^0-9]/g, '')); setMacroEdit(rebalanceMacros(m, mac.k, isNaN(v) ? 0 : v, sk)); }}
                                 style={{ width:40, background:'transparent', border:'none', color:text, fontSize:15, fontWeight:800, textAlign:'center', fontFamily:'inherit', fontVariantNumeric:'tabular-nums', outline:'none', padding:0 }} />
                          <span style={{ fontSize:9, color:muted, fontWeight:700 }}>g</span>
                        </span>
                        <button aria-label={`Increase ${mac.l}`} onClick={() => setMacroEdit(rebalanceMacros(m, mac.k, (m[mac.k]||0) + mac.step, sk))}
                                style={{ width:26, height:26, borderRadius:7, background:s1, border:`1px solid ${border}`, color:text, fontSize:15, lineHeight:1, cursor:'pointer', fontFamily:'inherit' }}>+</button>
                      </div>
                    </div>
                  ))}
                </div>
                {c.training_burn_kcal > 0 && (
                  <div style={statRow}>
                    <span style={{ color: muted, fontSize: 12, fontWeight: 700, display:'flex', alignItems:'center', gap: 6 }}>
                      Training day <span style={{ fontSize: 9, color: teal, fontWeight: 800, border:`1px solid ${teal}55`, borderRadius: 5, padding:'1px 4px' }}>⚡</span>
                    </span>
                    <span style={{ color: text, fontWeight: 800, fontVariantNumeric:'tabular-nums' }}>
                      {fmtKcal(sk + c.training_burn_kcal)} <span style={{ fontSize: 11, color: muted, fontWeight: 700 }}>kcal · +{fmtKcal(c.training_burn_kcal)}</span>
                    </span>
                  </div>
                )}
              </div>

              {/* 4 · AI view — coaching on the recommended target */}
              {stored.recommendation?.primary && (
                <div style={{ padding:'10px 12px', borderRadius: 10, background: s2, border:`1px solid ${tone.color}44` }}>
                  <div style={{ fontSize: 10, color: tone.color, fontWeight: 800, letterSpacing:'0.12em', marginBottom: 5 }}>{tone.emoji} AI VIEW · {tone.label.toUpperCase()}</div>
                  <div style={{ fontSize: 12, color: text, lineHeight: 1.5 }}>{stored.recommendation.primary.rationale}</div>
                  {alt && (
                    <button onClick={() => setSliderKcal(Math.round(alt.target_kcal))} style={{ ...chip(false, teal), marginTop: 8, fontSize: 11 }}>
                      Try alternative · {fmtKcal(alt.target_kcal)} kcal
                    </button>
                  )}
                  {dn.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      {dn.slice(0, 3).map((d, i) => (
                        <div key={i} style={{ fontSize: 11, color: muted, lineHeight: 1.45, marginTop: 2 }}>• {d}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 5 · Learning — it adapts as your data updates */}
              {(stored.tdeeRevisedFrom || stored.predictionCheck) && (
                <div style={{ padding:'10px 12px', borderRadius: 10, background: s2, border:`1px solid ${border}` }}>
                  <div style={sectionLabel}>Learning</div>
                  {stored.tdeeRevisedFrom && (
                    <div style={{ fontSize: 11.5, color: text, lineHeight: 1.5 }}>
                      TDEE revised <b style={{ color: gold }}>{fmtKcal(stored.tdeeRevisedFrom)} → {fmtKcal(stored.empiricalTDEE)}</b> as new data came in.
                    </div>
                  )}
                  {stored.predictionCheck && (() => {
                    const p = stored.predictionCheck;
                    const ok = Math.abs((p.predicted || 0) - (p.actual || 0)) <= 0.15;
                    return (
                      <div style={{ fontSize: 11.5, color: text, lineHeight: 1.5, marginTop: stored.tdeeRevisedFrom ? 5 : 0 }}>
                        Predicted <b>{ratePhrase(p.predicted)}</b> at {fmtKcal(p.intake)} kcal · actual <b>{ratePhrase(p.actual)}</b> <span style={{ color: ok ? teal : '#f0a86b' }}>{ok ? '✓ on track' : '— recalibrating'}</span>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* 6 · How this was calculated */}
              <button onClick={() => setExplanationOpen(o => !o)}
                      style={{ background:'transparent', border:'none', color: muted, fontSize: 12, fontWeight: 700, textAlign:'left', padding: 0, cursor:'pointer', fontFamily:'inherit' }}>
                {explanationOpen ? '▾' : '▸'} How this was calculated
              </button>
              {explanationOpen && (
                <div style={{ padding: 10, background: s2, border: `1px solid ${border}`, borderRadius: 8, fontSize: 12, color: text, lineHeight: 1.55 }}>
                  {/* Estimate basis — the two independent TDEE estimates + how the
                      weight-trend confidence blends them. */}
                  {stored._computed && (() => {
                    const cc = stored._computed;
                    const Item = ({ label, val, sub }) => (
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 9, color: muted, fontWeight: 800, letterSpacing:'0.08em', textTransform:'uppercase' }}>{label}</div>
                        <div style={{ fontSize: 14, color: val==null?muted:text, fontWeight: 800, fontVariantNumeric:'tabular-nums' }}>{val==null?'—':fmtKcal(val)}</div>
                        {sub && <div style={{ fontSize: 9, color: muted, fontWeight: 600 }}>{sub}</div>}
                      </div>
                    );
                    return (
                      <div style={{ marginBottom: 10, display:'flex', flexDirection:'column', gap: 8 }}>
                        <div style={{ display:'flex', gap: 10 }}>
                          <Item label="Energy balance" val={cc.tdeeBalance} sub={cc.observed_weekly_rate_kg!=null?`${cc.observed_weekly_rate_kg} kg/wk · ${cc.span_days}d`:'needs weigh-ins'} />
                          <Item label="Measured burn" val={cc.tdeeActivity} sub={cc.burn_days?`${cc.burn_days} days`:'no burn data'} />
                          <Item label="Formula" val={cc.formulaTDEE} sub="Mifflin × act" />
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 5, borderRadius: 3, background: border, overflow:'hidden' }}>
                            <div style={{ width: `${Math.round((cc.weight_trend_confidence||0)*100)}%`, height:'100%', background: teal }} />
                          </div>
                          <span style={{ fontSize: 10, color: muted, fontWeight: 700, whiteSpace:'nowrap' }}>
                            {Math.round((cc.weight_trend_confidence||0)*100)}% trend weight{cc.tdee_divergence_pct!=null ? ` · ${cc.tdee_divergence_pct>0?'+':''}${Math.round(cc.tdee_divergence_pct)}% gap` : ''}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                  Based on <strong>{stored._computed?.intake_days ?? stored.days_used ?? 0}</strong> days of food logging{stored._computed?.weight_readings != null && (<> and <strong>{stored._computed.weight_readings}</strong> weigh-in{stored._computed.weight_readings === 1 ? '' : 's'}{stored._computed.span_days ? ` over ${stored._computed.span_days}d` : ''}</>)} ({stored.burn_source.replace('_',' ')}). {stored.explanation}
                  {stored._computed && stored._computed.weight_readings != null && stored._computed.weight_readings < 5 && (
                    <div style={{ marginTop: 8, color: teal, fontSize: 11.5, lineHeight: 1.5 }}>
                      Only {stored._computed.weight_readings} weigh-in{stored._computed.weight_readings === 1 ? '' : 's'} logged — the weight-trend method needs ~2 weeks of regular weigh-ins to trust your own "ate X → lost Y" rate. Until then it leans on your measured burn. Weigh in daily (or sync Apple Health body composition) to sharpen it.
                    </div>
                  )}
                  {stored.deviation_note && (
                    <div style={{ marginTop: 8, color: '#f0a86b', fontSize: 11.5 }}>{stored.deviation_note}</div>
                  )}
                </div>
              )}

              <div style={{ display:'flex', justifyContent:'space-between', fontSize: 10.5, color: muted, fontFamily:'monospace', letterSpacing:'0.06em', marginTop: 4 }}>
                <span style={{ display:'inline-flex', alignItems:'baseline', gap: 6 }}>
                  Last: {fmtDate(stored.computedAt)}
                  <AICostBadge feature="aiCalorieTarget" prefix="·" fontSize={10} color={muted} />
                </span>
                <span>Next auto-update: {(() => {
                  try {
                    const d = new Date(stored.computedAt);
                    const day = d.getDay(); // 0 Sun
                    const offset = (7 - day) % 7 || 7;
                    const nxt = new Date(d.getTime() + offset * 86400000);
                    return fmtDate(nxt);
                  } catch { return '—'; }
                })()}</span>
              </div>
            </div>
            );
          })()}

          {/* Legacy record (computed before the redesign) — prompt a recompute */}
          {stored && !stored._computed && !loading && (
            <div style={{ borderTop:`1px solid ${border}`, paddingTop: 12, fontSize: 12, color: muted, lineHeight: 1.55 }}>
              Current target <strong style={{ color: gold }}>{fmtKcal(stored.dailyTarget)} kcal</strong>. Tap <strong>Recompute Now</strong> to load the new interactive view (live projection slider) with your latest data.
            </div>
          )}

          {/* Empty-state hints */}
          {!stored && !loading && (
            <div style={{ fontSize: 11.5, color: muted, lineHeight: 1.55 }}>
              Back-solves your real TDEE from up to 8 weeks of data — your weight-trend slope (least-squares, not endpoints) reconciled with measured Apple Health burn — then sets a daily target for your chosen rate. The more weigh-ins you log, the more it leans on your own trend over formulas.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Weekly auto-recompute (lazy trigger) ────────────────────────────────
// Called from the parent on app open / Calories mount. Fires at most once
// per Sunday-or-later week, requires ≥14 days of intake data, silently
// updates storage + goals. Returns the new record (or null if skipped).
export async function maybeAutoRecompute({ calorieLog, today, weightLog, wizardProfile, goalsRaw, saveGoals, showToast }) {
  try {
    const last = localStorage.getItem(LAST_AUTO_KEY);
    const now = new Date();
    if (last) {
      const ageMs = now.getTime() - new Date(last).getTime();
      if (ageMs < 6 * 86400000) return null; // less than 6d since last auto run
    }
    // Only fire on Sun (0) / Mon (1) / Tue (2) — enough slack if app wasn't opened on Sunday
    if (![0,1,2].includes(now.getDay())) return null;
    const series = extractSeries(calorieLog, 14);
    if (series.nWithIntake < 14) return null;
    const prefs = loadPrefs();
    const lossPerWeek = prefs.lossPerWeek === 1 ? 1 : 0.5;
    const burnSource  = prefs.burnSource || 'apple_health';
    const todayLog = calorieLog?.[today] || {};
    const bh = todayLog.bodyHistory || { weight: [], bodyFat: [] };
    const lastW = bh.weight?.[bh.weight.length - 1];
    const lastBF = bh.bodyFat?.[bh.bodyFat.length - 1];
    const manualLast = (weightLog || []).slice(-1)[0];
    const weight_kg = lastW?.value || manualLast?.kg || parseFloat(wizardProfile?.weight) || null;
    const body_fat_pct = lastBF?.value || null;
    const lean = (weight_kg && body_fat_pct) ? +(weight_kg * (1 - body_fat_pct/100)).toFixed(2) : null;
    const h = parseFloat(wizardProfile?.height) || 0;
    const bmi = (weight_kg && h) ? +(weight_kg / Math.pow(h/100, 2)).toFixed(2) : null;
    const current = { weight_kg, body_fat_pct, lean_mass_kg: lean, bmi };
    const profile = { age: wizardProfile?.age, sex: wizardProfile?.sex, height: wizardProfile?.height, weight: weight_kg, activity: wizardProfile?.activity || '1.55' };
    const payload = buildPayload({ profile, calorieLog, today, weightLog, goalWeightKg: parseFloat(prefs.goalWeightKg) || null, lossPerWeek, days: 56, current });
    let parsed;
    if (burnSource === 'formula') {
      parsed = assembleLocalRecord(payload.computed, lossPerWeek);
    } else {
      try {
        const raw = await callGemini(JSON.stringify(payload), {
          systemInstruction: SYSTEM_PROMPT, responseSchema: RESPONSE_SCHEMA,
          model: 'gemini-2.5-pro', maxOutputTokens: 8192, temperature: 0.3, feature: 'aiCalorieTarget',
        });
        parsed = reconcileWithComputed(JSON.parse(raw), payload.computed);
      } catch (e) {
        parsed = assembleLocalRecord(payload.computed, lossPerWeek);
      }
    }
    const record = { ...parsed, computedAt: new Date().toISOString(), goal_kg_per_week: lossPerWeek, burn_source: burnSource, days_used: payload.computed.intake_days };
    const prev = loadAITarget();
    saveAITarget(record);
    localStorage.setItem(LAST_AUTO_KEY, new Date().toISOString());
    if (saveGoals && goalsRaw) {
      const noManualOverride = !prev || Math.abs((goalsRaw.calories || 0) - (prev.dailyTarget || 0)) <= 5;
      if (noManualOverride) {
        saveGoals(goalsWithTarget(goalsRaw, record.dailyTarget, record.macros.protein_g, record.macros.carbs_g, record.macros.fat_g, payload.computed.training_burn_kcal));
      }
    }
    if (showToast && prev && Math.abs((prev.dailyTarget || 0) - record.dailyTarget) > 50) {
      const dir = record.dailyTarget > prev.dailyTarget ? '+' : '−';
      showToast(`Calorie target updated: ${fmtKcal(record.dailyTarget)} kcal (${dir}${Math.abs(Math.round(record.dailyTarget - prev.dailyTarget))})`);
    }
    return record;
  } catch (e) {
    console.warn('[AICalorieTarget] auto-recompute skipped:', e);
    return null;
  }
}

// Small inline banner for the Calories tab dashboard. Tappable to deep-link
// into Goals → AI Calorie Target.
export function AICalorieTargetBanner({ colors, onOpen }) {
  const stored = loadAITarget();
  if (!stored) return null;
  const { muted, border, s2, gold, text } = colors;
  return (
    <button onClick={onOpen}
            style={{ width:'100%', padding:'8px 12px', background: s2, border:`1px solid ${border}`, borderRadius: 10, color: text, display:'flex', justifyContent:'space-between', alignItems:'center', fontFamily:'inherit', cursor:'pointer', marginBottom: 8 }}>
      <span style={{ fontSize: 11, color: muted, letterSpacing:'0.08em', fontWeight: 700 }}>TODAY'S TARGET</span>
      <span style={{ fontSize: 13, color: gold, fontWeight: 800, fontVariantNumeric:'tabular-nums' }}>{fmtKcal(stored.dailyTarget)} kcal <span style={{ color: muted, fontSize: 10.5, fontWeight: 700 }}>· set in Goals ›</span></span>
    </button>
  );
}
